import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle,
  AlertCircle,
  Calendar,
  Banknote,
  Clock,
  User,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { SetupData } from "../JobSetupWizard";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { toast } from "@/components/ui/use-toast";

interface CommitResponse {
  success: boolean;
  message: string;
  error?: string;
  timeline_entry_id?: number;
  effective_date?: string;
  setup_summary?: any;
}

interface ReviewConfirmStepProps {
  employeeId: number;
  setupData: SetupData;
  onValidation: (isValid: boolean) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (submitting: boolean) => void;
}

export const ReviewConfirmStep: React.FC<ReviewConfirmStepProps> = ({
  employeeId,
  setupData,
  onValidation,
  onSubmit,
  isSubmitting,
  setIsSubmitting,
}) => {
  const [setupSummary, setSetupSummary] = useState<any>(null);
  const [commitResult, setCommitResult] = useState<{
    success: boolean;
    message: string;
    error?: string;
  } | null>(null);

  useEffect(() => {
    // Validate that all required data is present
    const isComplete = !!(
      setupData.startDate &&
      setupData.payType &&
      setupData.payRate &&
      setupData.salaryUnit &&
      setupData.payFrequency &&
      setupData.workingHours
    );

    onValidation(isComplete);

    if (isComplete) {
      generateSummary();
    }
  }, [setupData]);

  const generateSummary = () => {
    const totalHours = setupData.workingHours
      ? Object.values(setupData.workingHours).reduce((total, hours) => {
          return total + (parseFloat(hours || "0") || 0);
        }, 0)
      : 0;

    const workingDays = setupData.workingHours
      ? Object.values(setupData.workingHours).filter(
          (hours) => parseFloat(hours || "0") > 0,
        ).length
      : 0;

    setSetupSummary({
      startDate: setupData.startDate,
      payType: setupData.payType,
      payRate: setupData.payRate,
      salaryUnit: setupData.salaryUnit,
      payFrequency: setupData.payFrequency,
      totalWeeklyHours: totalHours,
      workingDays: workingDays,
      workingHours: setupData.workingHours,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy"); // UK date format
  };

  const formatPayRate = () => {
    const rate = parseFloat(setupData.payRate || "0");
    const unit = setupData.salaryUnit?.toLowerCase() || "";
    const frequency = setupData.payFrequency?.toLowerCase() || "";

    if (setupData.payType === "salary") {
      return `£${rate.toLocaleString("en-GB")} ${unit}, paid ${frequency}`;
    } else {
      return `£${rate.toFixed(2)}/hour, paid ${frequency}`;
    }
  };

  const getWorkingHoursSummary = () => {
    if (!setupData.workingHours) return "";

    const workingDays = Object.entries(setupData.workingHours)
      .filter(([_, hours]) => parseFloat(hours || "0") > 0)
      .map(
        ([day, hours]) =>
          `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours}h`,
      )
      .join(", ");

    return workingDays;
  };

  const calculateEquivalentRate = () => {
    if (!setupData.payRate || !setupSummary?.totalWeeklyHours) return null;

    const rate = parseFloat(setupData.payRate);
    const weeklyHours = setupSummary.totalWeeklyHours;

    if (setupData.payType === "salary" && weeklyHours > 0) {
      let annualSalary = rate;
      if (setupData.salaryUnit === "MONTHLY") {
        annualSalary = rate * 12;
      } else if (setupData.salaryUnit === "WEEKLY") {
        annualSalary = rate * 52;
      }
      const hourlyEquivalent = annualSalary / (weeklyHours * 52);
      return `£${hourlyEquivalent.toFixed(2)}/hour`;
    } else if (setupData.payType === "hourly") {
      const annualEquivalent = rate * weeklyHours * 52;
      return `£${annualEquivalent.toLocaleString("en-GB")}/year`;
    }

    return null;
  };

  const handleCommitSetup = async () => {
    setIsSubmitting(true);
    setCommitResult(null);

    try {
      const response = await ApiService.request<CommitResponse>(
        EndpointKeys.COMMIT_SETUP_WIZARD,
        {
          method: "POST",
          urlParams: { employeeId },
          body: {
            start_date: setupData.startDate,
            pay_type: setupData.payType,
            pay_rate: parseFloat(setupData.payRate!),
            salary_unit: setupData.salaryUnit,
            pay_frequency: setupData.payFrequency,
            working_hours: setupData.workingHours,
          },
        },
      );

      if (response.success) {
        setCommitResult({
          success: true,
          message: response.message,
        });

        toast({
          title: "Setup Complete!",
          description: "Job setup has been successfully committed.",
        });

        // Wait a moment to show success, then call onSubmit
        setTimeout(() => {
          onSubmit();
        }, 1500);
      } else {
        setCommitResult({
          success: false,
          message: response.error || "Setup commit failed",
        });
      }
    } catch (error: any) {
      console.error("Commit error:", error);
      setCommitResult({
        success: false,
        message: error.response?.data?.error || "Failed to commit setup",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!setupSummary) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Setup Summary Cards */}
      <div className="space-y-4">
        {/* Start Date */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Job Start Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium">
              {formatDate(setupData.startDate!)}
            </p>
            <p className="text-sm text-gray-600">
              {format(new Date(setupData.startDate!), "EEEE, dd MMMM yyyy")}
            </p>
          </CardContent>
        </Card>

        {/* Pay Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Banknote className="h-4 w-4" />
              Pay Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-lg font-medium">{formatPayRate()}</p>
              <Badge variant="outline" className="mt-1">
                {setupData.payType === "salary" ? "Salaried" : "Hourly"}{" "}
                Employee
              </Badge>
            </div>

            {calculateEquivalentRate() && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Equivalent:</strong> {calculateEquivalentRate()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Working Hours */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Working Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-4">
              <Badge variant="outline">
                {setupSummary.totalWeeklyHours} hours/week
              </Badge>
              <Badge variant="outline">
                {setupSummary.workingDays} working days
              </Badge>
            </div>

            <div className="text-sm text-gray-600">
              <p className="font-medium mb-1">Weekly schedule:</p>
              <p>{getWorkingHoursSummary()}</p>
            </div>

            {setupSummary.totalWeeklyHours > 48 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  This schedule exceeds 48 hours/week. Working Time Regulations
                  opt-out may be required.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Important Notes */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div>
            <p className="font-medium mb-2">Important:</p>
            <ul className="text-sm space-y-1">
              <li>• This information will be used for payroll calculations</li>
              <li>• Changes after setup may require additional approvals</li>
              <li>• All employment law compliance is your responsibility</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* Commit Result */}
      {commitResult && (
        <Alert
          className={
            commitResult.success
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          {commitResult.success ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription
            className={commitResult.success ? "text-green-800" : "text-red-800"}
          >
            <p className="font-medium">{commitResult.message}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Commit Button */}
      <div className="pt-4">
        <Button
          onClick={handleCommitSetup}
          disabled={isSubmitting || commitResult?.success}
          className="w-full bg-green-600 hover:bg-green-700"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Completing Setup...
            </>
          ) : commitResult?.success ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Setup Complete!
            </>
          ) : (
            "Complete Setup"
          )}
        </Button>
      </div>
    </div>
  );
};
